<template xmlns="http://www.w3.org/1999/html">
  <div>
    <HeadlineContainer headline="Create Project" :breadcrumbs="[{text: 'Projects'}, {text: 'Create'}]"/>

    <LoadingContainer v-if="loading"/>

    <div v-else class="container">
      <form @submit.prevent="submit">
        <div class="row">
          <div class="col-xs-6">
            <div class="help-text">
              <h3>
                Details
              </h3>

              <p>
                Enter the details of the project.
              </p>
            </div>
          </div>

          <div class="col-xs-18">
            <div class="panel panel-default">
              <div class="panel-body form-horizontal">
                <TextFormGroup
                    v-model="data.name"
                    name="name"
                    label="Name"
                    help-text="The name of the project."
                    :error="(errors.name || [])[0]"
                />

                <CkEditorFormGroup
                    v-model="data.description"
                    name="description"
                    label="Description"
                    help-text="The description of the project."
                    :error="(errors.description || [])[0]"
                />

                <CkEditorFormGroup
                    v-model="data.partner_description"
                    name="partner_description"
                    label="Partner Description"
                    help-text="The description of the project for the partner CMS."
                    :error="(errors.partner_description || [])[0]"
                />

                <RadioFormGroup
                    v-model="data.is_partner_accessible"
                    name="is_partner_accessible"
                    label="Partner Accessible"
                    help-text="Enable this option to make the project visible in the partner CMS. Disabling it will hide the project from partners."
                    :error="(errors.is_partner_accessible || [])[0]"
                    :options="[{value: true, label: 'Yes'}, {value: false, label: 'No'}]"
                />

                <DateFormGroup
                    v-model="data.content_deadline_at"
                    name="content_deadline_at"
                    label="Content Deadline"
                    help-text="The content deadline for the project."
                    :error="(errors.content_deadline_at || [])[0]"
                />

                <DateFormGroup
                    v-model="data.ends_at"
                    name="ends_at"
                    label="Publish Date"
                    help-text="The date the project is published."
                    :error="(errors.ends_at || [])[0]"
                />

                <GalleryFormGroup
                    v-model="data.partner_image_id"
                    name="partner_image_id"
                    label="Partner Image"
                    help-text="The image for the partner CMS presentation of the project."
                    :error="(errors.partner_image_id || [])[0]"
                    :nullable="true"
                />

                <SelectFormGroup
                    v-model="data.project_category_id"
                    name="project_category_id"
                    label="Project Category"
                    help-text="The category of the project."
                    :error="(errors.project_category_id || [])[0]"
                    :options="projectCategoryOptions"
                />

                <SelectFormGroup
                    v-model="data.portal_id"
                    name="portal_id"
                    label="Portal"
                    help-text="The portal that the project belongs to."
                    :error="(errors.portal_id || [])[0]"
                    :options="portalOptions"
                />
              </div>
            </div>
          </div>
        </div>

        <hr class="panel-separator">

        <div class="row">
          <div class="col-xs-6">
            <div class="help-text">
              <h3>
                Managers
              </h3>

              <p>
                Select the users that should manage the project.
              </p>

              <button type="button" class="btn btn-black" @click="data.project_managers.push({})">
                <i class="fa fa-plus"/> Add
              </button>
            </div>
          </div>

          <div class="col-xs-18">
            <div class="panel panel-default">
              <div class="panel-body form-horizontal">
                <SelectWithButton
                    v-for="(seller, index) in data.project_managers"
                    :key="index"
                    v-model="seller.user_id"
                    name="user_id"
                    label="User"
                    :error="(errors['project_managers.' + index + '.user_id'] || [])[0]"
                    :options="userOptions"
                    :button-text="'<i class=\'fa fa-minus\'></i> Remove'"
                    @click="data.project_managers.splice(index, 1)"
                />

                <div v-if="!data.project_managers.length" class="text-center">
                  <i>
                    There are no managers for the project.
                  </i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <hr class="panel-separator">

        <div class="row">
          <div class="col-xs-6">
            <div class="help-text">
              <h3>
                Sales Team
              </h3>

              <p>
                Select the users that should sell the project.
              </p>

              <button type="button" class="btn btn-black" @click="data.project_sellers.push({})">
                <i class="fa fa-plus"/> Add
              </button>
            </div>
          </div>

          <div class="col-xs-18">
            <div class="panel panel-default">
              <div class="panel-body form-horizontal">
                <SelectWithButton
                    v-for="(seller, index) in data.project_sellers"
                    :key="index"
                    v-model="seller.user_id"
                    name="user_id"
                    label="User"
                    :error="(errors['project_sellers.' + index + '.user_id'] || [])[0]"
                    :options="userOptions"
                    :button-text="'<i class=\'fa fa-minus\'></i> Remove'"
                    @click="data.project_sellers.splice(index, 1)"
                />

                <div v-if="!data.project_sellers.length" class="text-center">
                  <i>
                    There is no sales team for the project.
                  </i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <hr class="panel-separator">

        <div class="row">
          <div class="col-xs-6">
            <div class="help-text">
              <h3>
                Milestones
              </h3>

              <p>
                Add milestones to the project to plan progress.
              </p>

              <button type="button" class="btn btn-black" @click="data.project_milestones.push({project_milestone_managers: []})">
                <i class="fa fa-plus"/> Add
              </button>
            </div>
          </div>

          <div class="col-xs-18">
            <div v-for="(projectMilestone, index) in data.project_milestones" class="panel panel-default">
              <div class="panel-heading">
                <div class="pull-left">
                  <h5>
                    Milestone #{{ index + 1 }}
                  </h5>
                </div>

                <div class="pull-right">
                  <button type="button" style="margin-top: 8px" class="btn btn-xs btn-black" @click="projectMilestone.project_milestone_managers.push({})">
                    <i class="fa fa-plus"/> Add Manager
                  </button>

                  <button type="button" style="margin-top: 8px" class="btn btn-xs btn-black" @click="data.project_milestones.splice(index, 1)">
                    <i class="fa fa-minus"/> Remove
                  </button>
                </div>

                <div class="clearfix"/>
              </div>

              <div class="panel-body form-horizontal">
                <TextFormGroup
                    v-model="projectMilestone.name"
                    name="name"
                    label="Name"
                    help-text="The name of the milestone."
                    :error="(errors['project_milestones.' + index + '.name'] || [])[0]"
                />

                <DateFormGroup
                    v-model="projectMilestone.date"
                    name="date"
                    label="Date"
                    help-text="The date of the milestone."
                    :error="(errors['project_milestones.' + index + '.date'] || [])[0]"
                />

                <SelectWithButton
                    v-for="(projectMilestoneManager, managerIndex) in projectMilestone.project_milestone_managers"
                    :key="managerIndex"
                    v-model="projectMilestoneManager.user_id"
                    :name="'project_milestones.' + index + '.project_milestone_managers.' + managerIndex + '.user_id'"
                    :label="'Manager #' + (managerIndex + 1)"
                    :options="userOptions"
                    button-text="<i class='fa fa-minus'></i> Remove"
                    :error="(errors['project_milestones.' + index + '.project_milestone_managers.' + managerIndex + '.user_id'] || [])[0]"
                    @click="projectMilestone.project_milestone_managers.splice(index, 1)"
                />
              </div>
            </div>

            <div v-if="!data.project_milestones.length" class="panel panel-default">
              <div class="panel-body form-horizontal">
                <div class="text-center">
                  <i>
                    There are no milestones for the project.
                  </i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <hr class="panel-separator">

        <div class="row">
          <div class="col-xs-offset-6 col-xs-18">

            <button type="submit" class="btn btn-black" style="margin-bottom: 22px;">
              <i class="fa fa-save"></i> Save
            </button>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import http from '@/http'
import notify from '@/notify'
import DateFormGroup from '@/vendor/forms/FormGroups/Horizontal/Date'
import Errors from '@/mixins/Errors'
import HeadlineContainer from '@/components/Layout/HeadlineContainer'
import GalleryFormGroup from "../../../components/FormGroups/Horizontal/Gallery.vue";
import SelectFormGroup from '@/vendor/forms/FormGroups/Horizontal/Select'
import TextFormGroup from '@/vendor/forms/FormGroups/Horizontal/Text'
import CkEditorFormGroup from '@/vendor/forms/FormGroups/Horizontal/CkEditor'
import RepeatableFormGroup from "../../../components/Forms/RepeatableFormGroup.vue";
import SelectWithButton from "../../../components/FormGroups/Horizontal/SelectWithButton.vue";
import LoadingContainer from "../../../components/Layout/LoadingContainer.vue";
import RadioFormGroup from '@/vendor/forms/FormGroups/Horizontal/Radio'

export default {
  components: {RadioFormGroup, LoadingContainer, CkEditorFormGroup, SelectWithButton, RepeatableFormGroup, DateFormGroup, GalleryFormGroup, HeadlineContainer, SelectFormGroup, TextFormGroup},

  mixins: [Errors],

  data() {
    return {
      data: {
        project_milestones: [],
        project_managers: [],
        project_sellers: [],
      },

      users: null,
      portals: null,
      projectCategories: null,

      loading: true
    }
  },

  mounted() {
    this.load()
  },

  computed: {
    userOptions() {
      return this.users.map(user => ({value: user.id, label: user.attributes.full_name}))
    },

    portalOptions() {
      return this.portals.map(portal => ({value: portal.id, label: portal.attributes.name}))
    },

    projectCategoryOptions() {
      return this.projectCategories.map(projectCategory => ({value: projectCategory.id, label: projectCategory.attributes.name}))
    },
  },

  methods: {
    load() {
      Promise.all([
        http.get('/api/v2/users', {page_size: 100}),
        http.get('/api/v2/portals', {page_size: 100}),
        http.get('/api/v2/project-categories', {page_size: 100}),
      ]).then(responses => {
        this.users = responses[0].data.data

        this.portals = responses[1].data.data

        this.projectCategories = responses[2].data.data

        this.loading = false
      })
    },

    submit() {
      http.post('/api/v2/projects', this.data)
          .then(() => {
            notify.success('The project was saved')

            this.$router.push('/projects?filter[status]=active')
          })
          .catch(this.catchValidationErrors)
    }
  }
}
</script>
